import { useState, useEffect } from "react";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import SelectBuscable, { type OpcionDeSelect } from '@/shared/components/SelectBuscable';
import { apiService } from '@/services/core/api.service';
import { herramientasApi } from '@/features/almacen/api/herramientas.api';
import type { ToolResponseDto } from '@/features/almacen/api/herramientas.api';
import type { UserProfile } from '@/services/api';
import toast from "react-hot-toast";
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';

// ─── Styles ───────────────────────────────────────────────────────────────────

const INPUT_CLASS =
  "w-full px-3 py-2.5 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors";

const BORDER_STYLE = { border: "1px solid #E0E0E0", backgroundColor: "#F9F9F9" };

const TIPOS = [
  { value: "preventive", label: "Preventivo" },
  { value: "corrective", label: "Correctivo" },
];

// ─── Select ───────────────────────────────────────────────────────────────────

const SelectField = ({
  label,
  value,
  onChange,
  opciones,
  placeholder,
  required,
  canDelete,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  opciones: OpcionDeSelect[];
  placeholder?: string;
  required?: boolean;
  canDelete?: boolean;
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <SelectBuscable
      variante="formulario"
      opciones={opciones}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      canDelete={canDelete}
      placeholderDeBusqueda={`Buscar ${label.toLowerCase()}...`}
    />
  </div>
);

// ─── Section card ─────────────────────────────────────────────────────────────

const SectionCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className="bg-white rounded-xl border shadow-sm" style={{ borderColor: "#E0E0E0" }}>
    <div className="px-6 py-4 border-b" style={{ borderColor: "#E0E0E0" }}>
      <h2 className="text-base font-semibold text-gray-800">{title}</h2>
    </div>
    <div className="p-6">{children}</div>
  </div>
);

// ─── Form state ───────────────────────────────────────────────────────────────

interface FormState {
  toolId: string;
  tipo: string;
  fecha: string;
  destino: string;
  responsableId: string;
  comentario: string;
}

const EMPTY: FormState = {
  toolId:       "",
  tipo:         "",
  fecha:        "",
  destino:      "",
  responsableId:"",
  comentario:   "",
};

// ─── Main component ───────────────────────────────────────────────────────────

interface Props {
  onBack: () => void;
  onSubmit?: () => void;
  /** Herramienta ya elegida, cuando se entra desde una alerta del dashboard. */
  initialToolId?: string;
}

const RegistrarEntradaForm = ({ onBack, onSubmit, initialToolId }: Props) => {
  const [form, setForm]     = useState<FormState>(
    initialToolId ? { ...EMPTY, toolId: initialToolId } : EMPTY,
  );
  const [tools, setTools]   = useState<ToolResponseDto[]>([]);
  const [users, setUsers]   = useState<UserProfile[]>([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);

  const set = (field: keyof FormState) => (val: string) =>
    setForm((prev) => ({ ...prev, [field]: val }));

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [herramientas, usersData] = await Promise.all([
          herramientasApi.findAll({ limit: 100 }),
          apiService.request<UserProfile[]>("/users/public/all"),
        ]);
        setTools(herramientas.data);
        setUsers(usersData);
      } catch {
        toast.error("Error al cargar datos");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const isValid = form.toolId !== "" && form.tipo !== "" && form.fecha !== "" && form.destino !== "";

  const handleSubmit = async () => {
    if (!isValid || saving) return;
    setSaving(true);
    try {
      await apiService.request(
        `/almacen/herramientas/${form.toolId}/mantenimiento`,
        {
          method: "POST",
          body: JSON.stringify({
            type:        form.tipo,
            destination: form.destino,
            ...(form.responsableId && { responsibleUserId: form.responsableId }),
            sentAt:      form.fecha,
            ...(form.comentario && { entryComment: form.comentario }),
          }),
        }
      );
      toast.success("Entrada a mantenimiento registrada");
      onSubmit?.();
      onBack();
    } catch (err: unknown) {
      const e = err as { statusCode?: number; message?: string | string[] };
      if (e.statusCode === 409) {
        toast.error("La herramienta tiene una asignación activa. Primero devuélvela en Asignaciones y luego regístrala aquí.");
      } else {
        const msg = Array.isArray(e.message) ? e.message.join(" ") : (e.message ?? "Error al registrar");
        toast.error(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-40">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="mx-auto px-8 py-8">
      {/* Header */}
      <div className="flex items-start gap-3 mb-6">
        <button
          type="button"
          onClick={onBack}
          className={estiloDeBoton("secundario")}
        >
          <ArrowLeft className="w-4 h-4" />
          Volver
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mantenimiento</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Herramientas enviadas a taller o servicio técnico
          </p>
        </div>
      </div>

      {/* Información general */}
      <SectionCard title="Información general">
        <div className="space-y-4">

          {/* Herramienta */}
          <SelectField
            label="Herramienta"
            value={form.toolId}
            onChange={set("toolId")}
            required
            placeholder="Seleccionar herramienta"
            opciones={tools.map((t) => ({
              value: String(t.id),
              label: `${t.name} — ${t.serialCode}`,
            }))}
          />

          {/* Tipo + Fecha */}
          <div className="grid grid-cols-2 gap-4">
            <SelectField
              label="Tipo"
              value={form.tipo}
              onChange={set("tipo")}
              required
              placeholder="Seleccionar"
              opciones={TIPOS}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha de envío <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={form.fecha}
                onChange={(e) => set("fecha")(e.target.value)}
                className={INPUT_CLASS}
                style={{ ...BORDER_STYLE, colorScheme: "light" }}
              />
            </div>
          </div>

          {/* Destino + Responsable */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Destino / Taller <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.destino}
                onChange={(e) => set("destino")(e.target.value)}
                placeholder="Ej. Taller eléctrico norte"
                className={INPUT_CLASS}
                style={BORDER_STYLE}
              />
            </div>

            <SelectField
              label="Responsable"
              value={form.responsableId}
              onChange={set("responsableId")}
              opciones={[
                { value: "", label: "Sin responsable" },
                ...users.map((u) => ({
                  value: String(u.id),
                  label: `${u.firstName} ${u.lastName}`,
                })),
              ]}
            />
          </div>

          {/* Comentario */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Comentario
            </label>
            <textarea
              value={form.comentario}
              onChange={(e) => set("comentario")(e.target.value)}
              rows={3}
              placeholder="Ej. La herramienta no enciende"
              className={`${INPUT_CLASS} resize-none`}
              style={BORDER_STYLE}
            />
          </div>
        </div>
      </SectionCard>

      {/* Footer */}
      <div className="flex justify-end gap-3 mt-6">
        <button
          type="button"
          onClick={onBack}
          className={estiloDeBoton("secundario")}
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!isValid || saving}
          className={estiloDeBoton("primario")}
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          <Save className="w-4 h-4" />
          Guardar
        </button>
      </div>
    </div>
  );
};

export default RegistrarEntradaForm;
